import { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, Trash2, Edit2, X, Save, ChevronLeft, ChevronRight, Loader2, School } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';

const EVENT_TYPES = [
  { value: 'general',  label: 'General',    color: '#6b7280' },
  { value: 'holiday',  label: 'Holiday',    color: '#22c55e' },
  { value: 'exam',     label: 'Exam',       color: '#ef4444' },
  { value: 'sports',   label: 'Sports',     color: '#f59e0b' },
  { value: 'cultural', label: 'Cultural',   color: '#8b5cf6' },
  { value: 'ptm',      label: 'PTM',        color: '#3b82f6' },
];

const TYPE_MAP = Object.fromEntries(EVENT_TYPES.map(t => [t.value, t]));

interface PaEvent {
  id: number;
  title: string;
  description: string | null;
  event_date: string;
  end_date: string | null;
  event_type: string;
  class_id: number | null;
  class_name?: string | null;
}

interface PaClass { id: number; class_name: string; }

const emptyForm = { title: '', description: '', event_date: '', end_date: '', event_type: 'general', class_id: '' };

function parseDateParts(d: string) {
  const dt = new Date(d + 'T00:00:00');
  return {
    day:   dt.toLocaleDateString('en-IN', { day: '2-digit' }),
    month: dt.toLocaleDateString('en-IN', { month: 'short' }),
    full:  dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
  };
}

export default function EventsPage() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents]   = useState<PaEvent[]>([]);
  const [classes, setClasses] = useState<PaClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]  = useState<PaEvent | null>(null);
  const [form, setForm]        = useState({ ...emptyForm });
  const [saving, setSaving]    = useState(false);
  const [filterType, setFilterType] = useState<string>('all');

  const monthLabel = new Date(year, month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<PaEvent[]>(`${BASE}/events?month=${month}&year=${year}`);
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      toastMessage('Failed to load events', 'error');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiRequest<{ data: PaClass[] }>(`${BASE}/pa-classes`)
      .then(r => setClasses(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => {});
  }, []);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };
  const openEdit = (ev: PaEvent) => {
    setEditing(ev);
    setForm({
      title: ev.title,
      description: ev.description ?? '',
      event_date: ev.event_date,
      end_date: ev.end_date ?? '',
      event_type: ev.event_type,
      class_id: ev.class_id ? String(ev.class_id) : '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toastMessage('Title is required', 'error');
    if (!form.event_date)   return toastMessage('Start date is required', 'error');
    if (form.end_date && form.end_date < form.event_date) return toastMessage('End date cannot be before start date', 'error');

    setSaving(true);
    try {
      const payload = {
        title:       form.title.trim(),
        description: form.description.trim() || null,
        event_date:  form.event_date,
        end_date:    form.end_date || null,
        event_type:  form.event_type,
        class_id:    form.class_id ? parseInt(form.class_id) : null,
      };

      if (editing) {
        const res = await apiRequest(`${BASE}/events/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        if (!res.success) return toastMessage(res.message || 'Failed to update event', 'error');
        toastMessage('Event updated');
      } else {
        const res = await apiRequest(`${BASE}/events`, { method: 'POST', body: JSON.stringify(payload) });
        if (!res.success) return toastMessage(res.message || 'Failed to create event', 'error');
        toastMessage('Event created');
      }
      setShowForm(false);
      load();
    } catch {
      toastMessage('Failed to save event', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ev: PaEvent) => {
    if (!confirm(`Delete "${ev.title}"?`)) return;
    try {
      const res = await apiRequest(`${BASE}/events/${ev.id}`, { method: 'DELETE' });
      if (!res.success) return toastMessage(res.message || 'Failed to delete', 'error');
      toastMessage('Event deleted');
      load();
    } catch {
      toastMessage('Failed to delete', 'error');
    }
  };

  const filtered = filterType === 'all' ? events : events.filter(e => e.event_type === filterType);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>

      {/* Page Header */}
      <div className="page-head">
        <div className="page-head-icon">
          <Calendar className="w-6 h-6" />
        </div>
        <div className="page-head-text">
          <h1>Events Calendar</h1>
          <p>Manage school holidays, exams, and special events</p>
        </div>
        <div className="page-head-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)', padding: '4px 6px' }}>
            <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={prevMonth} title="Previous month">
              <ChevronLeft size={15} />
            </button>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', minWidth: 130, textAlign: 'center' }}>{monthLabel}</span>
            <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={nextMonth} title="Next month">
              <ChevronRight size={15} />
            </button>
          </div>
          <button className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={openCreate}>
            <Plus size={15} /> Add Event
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 32px', gap: 16, overflow: 'hidden', minHeight: 0 }}>

        {/* Type filter tabs */}
        <div className="seg" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
          <button className={filterType === 'all' ? 'active' : ''} onClick={() => setFilterType('all')}>
            All <span style={{ marginLeft: 4, opacity: 0.6, fontSize: 11 }}>({events.length})</span>
          </button>
          {EVENT_TYPES.map(t => {
            const count = events.filter(e => e.event_type === t.value).length;
            if (count === 0) return null;
            return (
              <button key={t.value} className={filterType === t.value ? 'active' : ''} onClick={() => setFilterType(t.value)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, display: 'inline-block', flexShrink: 0 }} />
                  {t.label}
                  <span style={{ opacity: 0.6, fontSize: 11 }}>({count})</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Events card list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <div className="center" style={{ padding: '64px 20px' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '64px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
              <Calendar size={40} style={{ opacity: 0.25, display: 'block', margin: '0 auto 12px' }} />
              <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: 14 }}>No events this month</p>
              <p style={{ fontSize: 13, margin: 0 }}>Click "Add Event" to create one.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(ev => {
                const t = TYPE_MAP[ev.event_type] ?? TYPE_MAP['general'];
                const isRange = ev.end_date && ev.end_date !== ev.event_date;
                const startParts = parseDateParts(ev.event_date);
                const endParts   = isRange ? parseDateParts(ev.end_date!) : null;

                return (
                  <div key={ev.id} style={{
                    display: 'flex', alignItems: 'stretch', gap: 0,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 10, overflow: 'hidden',
                    borderLeft: `4px solid ${t.color}`,
                    transition: 'box-shadow 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                  >
                    {/* Date block */}
                    <div style={{
                      width: 148, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '12px 14px', borderRight: '1px solid var(--border)',
                      background: `${t.color}08`,
                    }}>
                      {isRange ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: t.color, lineHeight: 1 }}>{startParts.day}</div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{startParts.month}</div>
                          </div>
                          <span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 500 }}>–</span>
                          <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: t.color, lineHeight: 1 }}>{endParts!.day}</div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{endParts!.month}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: t.color, lineHeight: 1 }}>{startParts.day}</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{startParts.month}</div>
                        </div>
                      )}
                    </div>

                    {/* Event info */}
                    <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.title}
                      </div>
                      {ev.description && (
                        <div style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {ev.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 700, color: t.color, textTransform: 'uppercase',
                          background: `${t.color}18`, borderRadius: 4, padding: '2px 7px', letterSpacing: 0.3,
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                          {t.label}
                        </span>
                        {ev.class_name ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-2)', background: 'var(--surface-2)', borderRadius: 4, padding: '2px 7px', fontWeight: 500 }}>
                            <School size={10} />
                            {ev.class_name}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Entire School</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 14px', flexShrink: 0 }}>
                      <button className="icon-btn" title="Edit" onClick={() => openEdit(ev)}>
                        <Edit2 size={14} />
                      </button>
                      <button className="icon-btn danger" title="Delete" onClick={() => handleDelete(ev)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <>
          <div className="scrim" onClick={() => setShowForm(false)} />
          <div className="modal">
            <div className="modal-head">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent-wash)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Calendar size={17} style={{ color: 'var(--accent-deep)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h2>{editing ? 'Edit Event' : 'Add Event'}</h2>
                <p>{editing ? 'Update the event details below' : 'Fill in the details to add a new event'}</p>
              </div>
              <button className="icon-btn" onClick={() => setShowForm(false)}>
                <X size={17} />
              </button>
            </div>

            <div className="modal-body col gap-16">
              {/* Title */}
              <div className="field">
                <label>Title <span className="req">*</span></label>
                <input
                  className="input"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Annual Sports Day"
                  autoFocus
                />
              </div>

              {/* Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  <label>Start Date <span className="req">*</span></label>
                  <input type="date" className="input" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} />
                </div>
                <div className="field">
                  <label>End Date <span className="opt">(optional)</span></label>
                  <input type="date" className="input" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
                </div>
              </div>

              {/* Type + Scope */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  <label>Event Type</label>
                  <select className="select" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                    {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Scope</label>
                  <select
                    className="select"
                    value={form.class_id === '' ? 'school' : 'class'}
                    onChange={e => setForm(f => ({ ...f, class_id: e.target.value === 'school' ? '' : (classes[0] ? String(classes[0].id) : '') }))}
                  >
                    <option value="school">Entire School</option>
                    <option value="class">By Class</option>
                  </select>
                </div>
              </div>

              {/* Class picker — shown only when By Class is selected */}
              {form.class_id !== '' && (
                <div className="field">
                  <label>Select Class</label>
                  <select className="select" value={form.class_id} onChange={e => setForm(f => ({ ...f, class_id: e.target.value }))}>
                    {classes.map(c => <option key={c.id} value={String(c.id)}>{c.class_name}</option>)}
                  </select>
                </div>
              )}

              {/* Description */}
              <div className="field">
                <label>Description <span className="opt">(optional)</span></label>
                <textarea
                  className="textarea"
                  rows={3}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Additional details about this event…"
                />
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 100 }}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? 'Saving…' : 'Save Event'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
